import type { LabelPrintBatchCreateRequest, LabelPrintBatchDetail, PurchaseOrder, PurchaseOrderStatus } from "@shared/types";
import { generateTestId, readableBatchId } from "@client-shared/lib/util";
import { getBatchStatusDisplay } from "./batch-status.utils";

export const setBatchSummaryTestId = (identifier: string): string => generateTestId("BatchSummary", identifier);

export const setBatchSummaryContainerTestId = (): string => setBatchSummaryTestId("container");

export const setBatchSummaryBatchIdTestId = (): string => setBatchSummaryTestId("batch-id");

export const setBatchSummaryStatusTestId = (): string => setBatchSummaryTestId("status");

export const setBatchSummaryMetricTestId = (metric: string): string => setBatchSummaryTestId(`metric-${metric}`);

export const setBatchSummaryActionTestId = (action: string): string => setBatchSummaryTestId(`action-${action}`);

export const setBatchSummaryOrderTestId = (purchaseOrderId: string): string => setBatchSummaryTestId(`order-${purchaseOrderId}`);

export const setBatchSummaryExportButtonTestId = (): string => setBatchSummaryTestId("export-button");

export const setBatchSummaryExportDialogTestId = (part: string): string => setBatchSummaryTestId(`export-dialog-${part}`);

export const getReadableBatchId = (batchId?: string): string => readableBatchId(batchId);

const RETRYABLE_ORDER_STATUSES: ReadonlySet<PurchaseOrderStatus> = new Set(["Error", "LabelError"]);

export const isBatchProcessing = (batch?: Pick<LabelPrintBatchDetail, "batchPrintStatus" | "batchStatus">): boolean => {
  return batch?.batchStatus === "Processing" || batch?.batchStatus === "Failed-for-Retry" || batch?.batchPrintStatus === "Processing";
};

export type RetryDisabledReason = "retrying" | "processing";

export const getRetryDisabledReason = (params: {
  isProcessing?: boolean;
  isRetrying?: boolean;
  batchStatus?: LabelPrintBatchDetail["batchStatus"];
}): RetryDisabledReason | undefined => {
  const { isProcessing, isRetrying, batchStatus } = params;

  if (isRetrying || batchStatus === "Failed-for-Retry") {
    return "retrying";
  }

  if (isProcessing) {
    return "processing";
  }

  return undefined;
};

export const isBatchDocumentsDisabled = (batch?: LabelPrintBatchDetail | null): boolean => {
  if (!batch) {
    return true;
  }

  const allOrdersErrored =
    (batch.purchaseOrders?.length ?? 0) > 0 &&
    batch.purchaseOrders.every(({ orderStatus }) => ["LabelError", "Error"].includes(`${orderStatus}`));

  return Boolean(isBatchProcessing(batch) || !["Completed", "PartiallyFailed"].includes(batch.batchStatus) || allOrdersErrored);
};

export const getBatchPurchaseOrderCarrierLabel = (purchaseOrder?: PurchaseOrder): string => {
  const carriers = new Set(
    (purchaseOrder?.lineItems ?? []).map(({ carrier }) => carrier).filter((carrier): carrier is string => Boolean(carrier))
  );

  if (carriers.size === 0) {
    return "—";
  }

  return Array.from(carriers).join(", ");
};

export const getBatchStatusBadgeClassName = (
  batchStatus?: LabelPrintBatchDetail["batchStatus"] | "PartiallyFailed" | (string & {})
): string => {
  return getBatchStatusDisplay(batchStatus).badgeClassName;
};

export const getBatchStatusLabel = (batchStatus?: LabelPrintBatchDetail["batchStatus"] | "PartiallyFailed" | (string & {})): string => {
  return getBatchStatusDisplay(batchStatus).label;
};

export const getBatchStatusLabelKey = (
  batchStatus?: LabelPrintBatchDetail["batchStatus"] | "PartiallyFailed" | (string & {})
): LabelPrintBatchDetail["batchStatus"] | "PartiallyFailed" | undefined => {
  return getBatchStatusDisplay(batchStatus).labelKey;
};

export const getRetryBatchRequest = (batch?: LabelPrintBatchDetail | null): LabelPrintBatchCreateRequest | undefined => {
  if (!batch?.batchId) {
    return undefined;
  }

  const orderIds =
    batch.purchaseOrders
      .filter(({ orderStatus }) => (orderStatus ? RETRYABLE_ORDER_STATUSES.has(orderStatus) : false))
      .map(({ purchaseOrderId }) => purchaseOrderId) ?? [];

  if (orderIds.length === 0) {
    return undefined;
  }

  return {
    batchId: batch.batchId,
    orderIds,
  };
};
